import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { fetchManualsTree, fetchPresignedUrl } from '../services/docsService';
import type { DocsState, TreeNode, S3Tree } from '../types/docsTypes';

// Convert Django tree → AntD tree format
function convertToAntdTree(tree: S3Tree, parentPath = ""): TreeNode[] {
    const result: TreeNode[] = [];

    // Handle files at current level
    const files = (tree as any).__files__;
    if (files) {
        files.forEach((file: string) => {
            result.push({
                title: file,
                key: parentPath ? `${parentPath}/${file}` : file,
                isLeaf: true
            });
        });
    }

    // Handle folders (skip __files__ key)
    Object.entries(tree).forEach(([key, value]) => {
        if (key === "__files__") return;

        const fullPath = parentPath ? `${parentPath}/${key}` : key;

        // Recursively get children (files + subfolders)
        const children = convertToAntdTree(value as S3Tree, fullPath);

        result.push({
            title: key,
            key: fullPath,
            isLeaf: false,
            children
        });
    });

    return result;
}

const initialState: DocsState = {
    treeData: [],
    loading: false,
    error: null,
    presignedUrl: null,
    presignedUrlLoading: false,
    presignedUrlError: null,
};

const docsSlice = createSlice({
    name: 'docs',
    initialState,
    reducers: {
        clearDocsError(state) {
            state.error = null;
            state.presignedUrlError = null;
        },
        clearPresignedUrl(state) {
            state.presignedUrl = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchManualsTree.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchManualsTree.fulfilled, (state, action: PayloadAction<S3Tree>) => {
                state.loading = false;
                state.treeData = convertToAntdTree(action.payload);
            })
            .addCase(fetchManualsTree.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchPresignedUrl.pending, (state) => {
                state.presignedUrlLoading = true;
                state.presignedUrlError = null;
            })
            .addCase(fetchPresignedUrl.fulfilled, (state, action) => {
                state.presignedUrlLoading = false;
                state.presignedUrl = action.payload.url;
            })
            .addCase(fetchPresignedUrl.rejected, (state, action) => {
                state.presignedUrlLoading = false;
                state.presignedUrlError = action.payload as string;
            });
    },
});

export const { clearDocsError, clearPresignedUrl } = docsSlice.actions;
export default docsSlice.reducer;
